/**
 * WidgetFirstCard Component
 *
 * This component renders a card widget with a header and a list of items. Each item includes an image, title, and publish date.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.headerTitle - The title for the widget header. This is required.
 * @param {string} [props.viewAllLink] - The URL for the "View All" link in the header.
 * @param {'image'|'text'} [props.variant] - 'image': thumbnail + title rows, 'text': title (+ description on first item) + date rows.
 * @param {Array<Object>} props.data - An array of data objects to be displayed in the widget. Each object should follow the format:
 * {
 *   title: "",
 *   description: "",
 *   publishDate: "",
 *   image: {
 *     url: "",
 *     altText: ""
 *   }
 * }
 *
 * @returns {JSX.Element} The rendered WidgetFirstCard component.
 */

import PropTypes from 'prop-types'
import WidgetCardHeader from '../widgetHeader'
import styles from './style.module.scss'
import widgetStyles from '../widgets.module.scss'
import MyImage from '@shared/components/myImage'
import { convertDt24h, dateCheck } from '@shared-submodule/utils'
import CustomLink from '@shared/components/customLink'

const WidgetFirstCard = ({ headerTitle, viewAllLink, data, viewAllInternalLink = false, cardInternalLink = false, variant = 'image' }) => {
  const renderCardItem = (item, idx) =>
    variant === 'text' ? (
      <div className={`${styles.widget_card_item} ${styles.text_item} d-flex flex-column`}>
        <h3 className={styles.widget_card_title}>{item?.title}</h3>
        {idx === 0 && item?.description && <p className={styles.widget_card_desc}>{item?.description}</p>}
        <p className={styles.widget_card_date}>{convertDt24h(dateCheck(item?.publishDate))}</p>
      </div>
    ) : (
      <div className={`${styles.widget_card_item} d-flex align-items-start`}>
        <div className={styles.widget_card_image}>
          <MyImage layout="fill" src={item?.image?.url} alt={item?.image?.altText || 'widget Image'} />
        </div>
        <h3 className={styles.widget_card_title}>{item?.title}</h3>
      </div>
    )
  return (
    <div className={`${widgetStyles.card} ${styles.widget_card} ${styles[variant]}`}>
      <WidgetCardHeader headerText={headerTitle} link={viewAllLink} internalLink={viewAllInternalLink} />
      {data?.map((item, idx) =>
        cardInternalLink ? (
          <CustomLink href={item?.sSlug || ''} key={item?.sSlug || idx}>
            <a className="d-block">{renderCardItem(item, idx)}</a>
          </CustomLink>
        ) : (
          <a href={item?.sSlug || ''} key={item?.sSlug || idx} className="d-block">
            {renderCardItem(item, idx)}
          </a>
        )
      )}
    </div>
  )
}

WidgetFirstCard.propTypes = {
  headerTitle: PropTypes.string.isRequired,
  viewAllLink: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object),
  viewAllInternalLink: PropTypes.bool,
  cardInternalLink: PropTypes.bool,
  variant: PropTypes.oneOf(['image', 'text'])
}

export default WidgetFirstCard
